import { Link } from "@components/Link/Link";
import { ImageWrapper } from "@components/Styled/ImageWrapper";
import { styledWithInvalidProps } from "@components/Styled/StyledWithInvalidProps";
import { VisuallyHidden } from "ariakit";
import React, { useMemo } from "react";
import tw from "twin.macro";
import { SearchResultsGiftCardPrice } from "./SearchResultsGiftCardPrice";
import { SearchResultsProductPrice } from "./SearchResultsProductPrice";
import {
  SearchResultsProductCardOutputProps,
  withSearchResultsProductCard,
} from "./withSearchResultsProductCard";
import {
  ProductCollectionCard,
  IconHeartFilled,
  IconHeart,
  Badge as BadgeUI,
} from "@bared/ui";

import { ProductCardVisibilityObserver } from "~/components/ProductCardVisibilityObserver";

import { useSite } from "~/hooks/useSite";
import { useWishlistToggle } from "~/hooks/useWishlist";

import { KlaviyoItemResolver } from "~/lib/KlaviyoItemResolver";
import { GoogleAnalyticsItemResolver } from "~/lib/GoogleAnalyticsItemResolver";

const Wrapper = tw.article``;

const Details = tw(Link)`
  flex flex-wrap pt-micro pb-micro focus-visible:!outline-offset-[-1px] lg:pt-mini lg:pb-0
`;

const Detail = tw.div`
  flex flex-col
`;

const TextWrapper = tw.div`
  flex mb-pico pr-pico md:pr-0
`;

const Text = styledWithInvalidProps("span")(({ bold, block, hidden }) => [
  tw`text-caption leading-h2 md:text-hint pl-micro lg:pl-0`,
  bold ? tw`font-medium` : null,
  block ? tw`block` : tw`inline`,
  hidden ? tw`block w-0 h-0 opacity-0` : null,
]);

const StyledPrice = tw(SearchResultsProductPrice)`
  text-caption leading-h2 md:mt-0 md:text-hint
`;

const StyledGiftCardPrice = tw(SearchResultsGiftCardPrice)`
  text-caption leading-h2 md:mt-0 md:text-hint
`;

const Badge = styledWithInvalidProps("span")(({ colour, sale }) => [
  tw`absolute left-micro top-micro text-caption leading-h5 px-mini md:left-mini md:top-mini md:text-hint md:leading-double z-10`,
  colour === `dark` ? tw`text-white bg-primary` : tw`text-primary bg-white`,
  sale ? tw`md:px-maxi` : null,
]);

const ItemImageWrapper = tw(ImageWrapper)`
  relative w-full
`;

const IconWrapper = tw.div`flex text-primary md:max-w-auto`;

export const SearchResultsProductCard = ({
  badge,
  price,
  compareAtPrice,
  collection,
  hiddenTitle,
  hoverImage,
  image,
  isGiftCard,
  link,
  product,
  productGroup,
  subTitle,
  tags,
  title,
  pagePosition,
}: SearchResultsProductCardOutputProps) => {
  const site = useSite();

  const klaviyoItem = useMemo(() => {
    return new KlaviyoItemResolver({
      site,
    }).resolveFromReactifyProduct({
      reactifyProduct: product,
    });
  }, [product, site]);

  const googleAnalyticsItem = useMemo(() => {
    return new GoogleAnalyticsItemResolver({
      site,
    }).resolveFromReactifyProduct({
      reactifyProduct: product,
    });
  }, [product, site]);

  const { isInWishlist, toggleWishlist } = useWishlistToggle({
    klaviyoItem,
    googleAnalyticsItem,
    productHandle: product.handle,
  });

  return (
    <ProductCardVisibilityObserver
      index={pagePosition - 1}
      googleAnalyticsItem={googleAnalyticsItem}
    >
      {({ ref, onClick }) => (
        <Wrapper data-testid="search-result-product-card" ref={ref}>
          <ItemImageWrapper className="group" image={image?.src}>
            <Link
              state={{ collection }}
              to={link?.url}
              title={link?.title}
              onClick={() => onClick()}
              data-product-handle={product.handle}
            >
              <ProductCollectionCard
                {...product}
                data={product}
                image={image?.src}
                hoverImage={hoverImage?.src}
                sizes="33vw"
              />
            </Link>
            <button
              onClick={toggleWishlist}
              className="absolute right-zero top-zero z-10 p-mini text-primary"
            >
              {!!isInWishlist ? (
                <IconHeartFilled className="w-[25px]" />
              ) : (
                <IconHeart className="w-[25px]" />
              )}
            </button>
            {badge ? (
              <BadgeUI
                children={badge?.name as string}
                size="small"
                {...badge}
                className="absolute left-mini top-mini z-10"
              />
            ) : null}
          </ItemImageWrapper>
          <Details
            state={{ collection: collection }}
            to={link?.url}
            title={link?.title}
            onClick={onClick}
          >
            <Detail className="w-2/3">
              <Text bold block>
                {title}
              </Text>
              <TextWrapper>
                <Text className="xl:mr-0 mr-pico">{subTitle}</Text>
                <VisuallyHidden>{hiddenTitle}</VisuallyHidden>
              </TextWrapper>
            </Detail>
            <Detail className="xl:pr-0 w-1/3 items-end pr-pico">
              <IconWrapper>
                <div
                  className="ruk_rating_snippet ruk_rating_snippet--clp empty:mb-0 mb-pico block"
                  data-sku={productGroup}
                ></div>
              </IconWrapper>
              {!isGiftCard ? (
                <StyledPrice price={price} compareAtPrice={compareAtPrice} />
              ) : (
                <StyledGiftCardPrice tags={tags} />
              )}
            </Detail>
          </Details>
        </Wrapper>
      )}
    </ProductCardVisibilityObserver>
  );
};

export default withSearchResultsProductCard(SearchResultsProductCard);
